import { useState, useRef } from 'react';
import './FileUpload.css';

const API_URL = import.meta.env.VITE_API_URL || '';

const FileUpload = ({ onUploadComplete, accept = "image/*,video/*", label = "Upload File" }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [inputId] = useState(`file-upload-${Math.random().toString(36).substring(7)}`);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (50MB max)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setError('File size must be less than 50MB');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setProgress(10);

      // Read file as base64
      const reader = new FileReader();
      
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          const percentLoaded = Math.round((e.loaded / e.total) * 50); // 0-50%
          setProgress(percentLoaded);
        }
      };

      reader.onload = async (e) => {
        try {
          setProgress(50);
          
          // Get base64 data (remove data:mime;base64, prefix)
          const base64Data = e.target.result.split(',')[1];

          // Get JWT token
          const token = localStorage.getItem('adminToken');
          if (!token) {
            throw new Error('Authentication required');
          }

          setProgress(60);

          // Upload to Vercel Blob
          const response = await fetch(`${API_URL}/api/upload`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              filename: file.name,
              contentType: file.type,
              file: base64Data
            })
          });

          setProgress(80);

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Upload failed');
          }

          setProgress(100);

          // Call the callback with the CDN URL
          if (onUploadComplete) {
            onUploadComplete(data.url);
          }

          // Reset after a short delay
          setTimeout(() => {
            setUploading(false);
            setProgress(0);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }, 1000);

        } catch (err) {
          console.error('Upload error:', err);
          setError(err.message || 'Upload failed');
          setUploading(false);
          setProgress(0);
        }
      };

      reader.onerror = () => {
        setError('Failed to read file');
        setUploading(false);
        setProgress(0);
      };

      reader.readAsDataURL(file);

    } catch (err) {
      console.error('File selection error:', err);
      setError(err.message || 'Failed to process file');
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="file-upload-container">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        disabled={uploading}
        className="file-input"
        id={inputId}
      />
      
      <label 
        htmlFor={inputId}
        className={`file-upload-button ${uploading ? 'uploading' : ''}`}
      >
        {uploading ? (
          <span className="upload-status">
            <span className="upload-spinner"></span>
            Uploading... {progress}%
          </span>
        ) : (
          <span className="upload-label">
            📤 {label}
          </span>
        )}
      </label>

      {uploading && (
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}

      {error && (
        <div className="upload-error">
          ❌ {error}
        </div>
      )}
    </div>
  );
};

export default FileUpload;

